/**
 * useInbox — Inbox state management hook.
 *
 * Architecture:
 *   - Initial conversation list and message history are fetched via HTTP (InboxService).
 *   - Real-time updates are delivered through a persistent WebSocket backed by Redis Pub/Sub.
 *   - NO setInterval polling. RAM and network usage are minimal.
 *
 * WebSocket event contract (mirrors backend inbox_events.py):
 *   NEW_MESSAGE          → append bubble to active conversation's message list
 *   CONVERSATION_UPDATED → update last_message / can_reply in the sidebar
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ChatConversation, InboxMessage } from '../domain/types';
import { InboxService } from '../infrastructure/inbox.service';
import { createInboxWebSocket, InboxWebSocketHandle } from '../infrastructure/inbox.websocket';

export const useInbox = () => {
  const normalizePhonePart = (value?: string) => (value || '').replace(/[^\d]/g, '');
  const normalizeConversationKey = (key?: string) => {
    if (!key) return '';
    const contactPart = key.split('|')[0];
    return normalizePhonePart(contactPart);
  };

  const normalizeText = (value?: string) => (value || '').trim().toLowerCase();

  const messageTimestamp = (msg: InboxMessage) => {
    const ts = msg.received_at ? Date.parse(msg.received_at) : NaN;
    return Number.isNaN(ts) ? 0 : ts;
  };

  const dedupeAndSortMessages = (items: InboxMessage[]): InboxMessage[] => {
    const stableById = new Map<string, InboxMessage>();
    const unstable: InboxMessage[] = [];

    items.forEach((msg) => {
      const id = msg._id || '';
      if (id && !id.startsWith('temp-')) {
        stableById.set(id, msg);
      } else {
        unstable.push(msg);
      }
    });

    const stableValues = Array.from(stableById.values());

    const filteredUnstable = unstable.filter((candidate) => {
      const candidateId = candidate._id || '';
      if (!candidateId.startsWith('temp-')) return true;

      return !stableValues.some((stable) => {
        const sameDirection = (stable.direction || 'received') === (candidate.direction || 'received');
        const samePhone = normalizeConversationKey(stable.from_number) === normalizeConversationKey(candidate.from_number);
        const sameContent = normalizeText(stable.content) === normalizeText(candidate.content);
        const closeInTime = Math.abs(messageTimestamp(stable) - messageTimestamp(candidate)) <= 30000;
        return sameDirection && samePhone && sameContent && closeInTime;
      });
    });

    const fallbackMap = new Map<string, InboxMessage>();
    filteredUnstable.forEach((msg) => {
      const key = [
        msg.direction || 'received',
        normalizeConversationKey(msg.from_number),
        normalizeText(msg.content),
        Math.floor(messageTimestamp(msg) / 1000).toString(),
        msg.message_type || 'text',
      ].join('|');

      const current = fallbackMap.get(key);
      if (!current) {
        fallbackMap.set(key, msg);
        return;
      }

      const currentIsTemp = (current._id || '').startsWith('temp-');
      const nextIsStable = !!msg._id && !msg._id.startsWith('temp-');
      if (currentIsTemp && nextIsStable) {
        fallbackMap.set(key, msg);
      }
    });

    return [...stableValues, ...Array.from(fallbackMap.values())].sort(
      (a, b) => messageTimestamp(a) - messageTimestamp(b)
    );
  };

  const mergeMessages = (prev: InboxMessage[], incoming: InboxMessage[]) =>
    dedupeAndSortMessages([...prev, ...incoming]);

  const sortConversationsByRecent = (items: ChatConversation[]): ChatConversation[] => {
    const parseTime = (value?: string) => {
      if (!value) return 0;
      const ts = Date.parse(value);
      return Number.isNaN(ts) ? 0 : ts;
    };
    return [...items].sort(
      (a, b) => parseTime(b.last_message_at) - parseTime(a.last_message_at)
    );
  };

  // ── Conversations (Left Panel) ───────────────────────────────────────────────
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | undefined>(undefined);
  const [conversationSearch, setConversationSearch] = useState<string>('');
  const [loadingConversations, setLoadingConversations] = useState(false);

  // ── Real-time activity signal ─────────────────────────────────────────────────
  // Increments each time an incoming message arrives via WebSocket.
  // The sidebar watches this to reset its pagination to page 1 so the
  // updated conversation always appears at the top (WhatsApp-style).
  const [incomingMessageTick, setIncomingMessageTick] = useState(0);

  // ── Messages of the active conversation (Right Panel) ────────────────────────
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 50;

  // ── Derived state ────────────────────────────────────────────────────────────
  const selectedConversation = conversations.find(
    c => normalizeConversationKey(c.phone_number) === normalizeConversationKey(selectedPhoneNumber)
  ) || (selectedPhoneNumber ? {
    phone_number: selectedPhoneNumber,
    can_reply: true, // Optimistic fallback
    unread_count: 0,
    direction: 'received' as const,
  } : undefined);

  // Track the currently selected phone number inside WebSocket callbacks without closure issues
  const selectedPhoneRef = useRef<string | undefined>(undefined);
  selectedPhoneRef.current = selectedPhoneNumber;

  // ── HTTP: fetch initial conversation list ────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const data = await InboxService.getConversations(conversationSearch);
      setConversations(sortConversationsByRecent(data));
    } catch (error) {
      console.error('Error fetching conversations', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [conversationSearch]);

  // Load conversations on mount and when search term changes
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // ── HTTP: fetch message history when selecting a conversation ────────────────
  // pageRef lets the loadMore callback always see the latest page without
  // re-creating fetchMessages (which would break the selectedPhoneNumber effect).
  const pageRef = useRef<number>(1);

  const fetchMessages = useCallback(async (reset = false) => {
    if (!selectedPhoneRef.current) return;
    setLoadingMessages(true);
    const targetPage = reset ? 1 : pageRef.current;

    try {
      const data = await InboxService.getConversationMessages(
        selectedPhoneRef.current,
        targetPage,
        pageSize
      );

      console.log('[Inbox] messages loaded:', data.messages.length, 'for', selectedPhoneRef.current);

      // Chat convention: oldest message at top → reverse the desc-sorted API response
      const reversedMessages = [...data.messages].reverse();

      if (reset) {
        pageRef.current = 2;
        setPage(2);
        setMessages(dedupeAndSortMessages(reversedMessages));
      } else {
        pageRef.current = targetPage + 1;
        setPage(prev => prev + 1);
        setMessages(prev => mergeMessages(prev, reversedMessages));
      }
      setHasMore(data.has_more);
    } catch (error) {
      console.error('[Inbox] Error fetching messages', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);
  // ↑ No deps: uses refs for phone/page so the effect below only fires on mount.

  // Reset and load messages whenever the selected conversation changes
  useEffect(() => {
    if (selectedPhoneNumber) {
      setMessages([]);
      pageRef.current = 1;
      setPage(1);
      setHasMore(false);
      fetchMessages(true);

      // ✅ Nuevo: Marcar como leído en el servidor y limpiar localmente
      InboxService.markAsRead(selectedPhoneNumber).catch(console.error);
      
      const targetPhone = normalizeConversationKey(selectedPhoneNumber);
      setConversations(prev => prev.map(c => 
        normalizeConversationKey(c.phone_number) === targetPhone ? { ...c, unread_count: 0 } : c
      ));

    } else {
      setMessages([]);
    }
  }, [selectedPhoneNumber, fetchMessages]);

  // ── WebSocket: real-time push (replaces all setInterval polling) ─────────────
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const handle: InboxWebSocketHandle = createInboxWebSocket(token, {
      onNewMessage: (msg: InboxMessage) => {
        const msgConversation = normalizeConversationKey(msg.from_number);
        const currentSelected = normalizeConversationKey(selectedPhoneRef.current);
        const isReceived = (msg.direction ?? 'received') === 'received';

        // Only append if the message belongs to the currently open conversation
        if (msgConversation === currentSelected && currentSelected !== '') {
          setMessages(prev => mergeMessages(prev, [msg]));

          // ✅ If we are actively looking at this chat, mark it as read immediately
          if (isReceived) {
            InboxService.markAsRead(msg.from_number).catch(console.error);
          }
        }

        // Update sidebar — add the conversation if it's brand new (first-time sender)
        setConversations(prev => {
          const exists = prev.some(c => normalizeConversationKey(c.phone_number) === msgConversation);
          let next: ChatConversation[];
          if (exists) {
            next = prev.map(c =>
              normalizeConversationKey(c.phone_number) === msgConversation
                ? { 
                    ...c, 
                    last_message: msg.content ?? `[${msg.message_type}]`, 
                    last_message_at: msg.received_at,
                    direction: (msg.direction ?? c.direction) as 'received' | 'sent',
                    unread_count:
                      msgConversation === currentSelected
                        ? 0
                        : (isReceived ? (c.unread_count || 0) + 1 : (c.unread_count || 0))
                  }
                : c
            );
          } else {
            // First message from this contact — prepend a new conversation card.
            // can_reply only opens on customer inbound; echoes/sent must not unlock the composer.
            next = [
              {
                phone_number: msg.from_number,
                last_message: msg.content ?? `[${msg.message_type}]`,
                last_message_at: msg.received_at,
                last_received_at: isReceived ? msg.received_at : undefined,
                can_reply: isReceived,
                direction: (msg.direction ?? 'received') as 'received' | 'sent',
                unread_count: isReceived && msgConversation !== currentSelected ? 1 : 0,
              } as ChatConversation,
              ...prev
            ];
          }
          return sortConversationsByRecent(next);
        });

        // Signal the sidebar to jump back to page 1 so the updated
        // conversation is always visible at the top (WhatsApp-style).
        setIncomingMessageTick(t => t + 1);
      },

      onConversationUpdated: (update) => {
        const updateConversation = normalizeConversationKey(update.phone_number);
        const currentSelected = normalizeConversationKey(selectedPhoneRef.current);

        setConversations(prev => {
          const conversationIndex = prev.findIndex(
            c => normalizeConversationKey(c.phone_number) === updateConversation
          );
          
          let next: ChatConversation[];
          if (conversationIndex !== -1) {
            // Update existing conversation
            next = prev.map((c, i) =>
              i === conversationIndex
                ? {
                    ...c,
                    contact_name: update.contact_name || c.contact_name,
                    last_message: update.last_message ?? c.last_message,
                    last_message_at: update.last_received_at ?? c.last_message_at,
                    can_reply: update.can_reply,
                    last_received_at: update.last_received_at,
                    unread_count: updateConversation === currentSelected ? 0 : (c.unread_count || 0)
                  }
                : c
            );
          } else {
            // Brand-new conversation (first-time sender)
            next = [
              {
                phone_number: update.phone_number,
                contact_name: update.contact_name,
                last_message: update.last_message,
                last_message_at: update.last_received_at,
                last_received_at: update.last_received_at,
                can_reply: update.can_reply,
                direction: 'received',
                unread_count: updateConversation === currentSelected ? 0 : 1
              } as ChatConversation,
              ...prev
            ];
          }
          return sortConversationsByRecent(next);
        });
      },

      onMessageStatusUpdated: (update) => {
        const docId = update?._id;
        if (!docId) return;
        setMessages((prev) =>
          prev.map((m) => {
            if (m._id !== docId) return m;
            return {
              ...m,
              delivery_status: update.delivery_status ?? m.delivery_status,
              status: (update.status as InboxMessage['status']) ?? m.status,
            };
          })
        );
      },
    });

    // Cleanup: intentionally close the socket when the Inbox page unmounts
    return () => handle.close();
  }, []);
  // ↑ Empty deps intentional: create ONE socket for the lifetime of the Inbox page.
  //   selectedPhoneRef lets callbacks always see the latest value without re-creating the socket.

  // ── Load older messages (pagination / scroll-to-top) ─────────────────────────
  const loadMore = useCallback(() => {
    if (!loadingMessages && hasMore) {
      fetchMessages(false);
    }
  }, [loadingMessages, hasMore, fetchMessages]); // fetchMessages is stable (empty deps)

  // ── Send message ─────────────────────────────────────────────────────────────
  const sendMessage = async (text: string) => {
    if (!selectedPhoneNumber) return;
    try {
      const tempId = `temp-${Date.now()}`;
      const tempMsg: InboxMessage = {
        _id: tempId,
        from_number: selectedPhoneNumber,
        content: text,
        message_type: 'text',
        received_at: new Date().toISOString(),
        direction: 'sent',
      };
      setMessages(prev => mergeMessages(prev, [tempMsg]));

      const res = await InboxService.sendMessage(selectedPhoneNumber, text);

      const stableId = (res?.doc_id || res?.message_id) as string | undefined;
      if (res.success && stableId) {
        setMessages(prev => {
          const normalized = prev.map(m => (m._id === tempId ? { ...m, _id: stableId } : m));
          return dedupeAndSortMessages(normalized);
        });
      }
    } catch (e) {
      console.error('Error sending message', e);
    }
  };

  const [sendingMedia, setSendingMedia] = useState(false);

  const sendMediaFiles = async (
    files: File[],
    caption?: string,
    voiceFlags?: boolean[]
  ) => {
    if (!selectedPhoneNumber || !files.length) return;

    setSendingMedia(true);
    try {
      const { validateOutboundFile, mapMediaUploadError } = await import('../domain/mediaLimits');

      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const isVoice = !!voiceFlags?.[index];
        const validation = validateOutboundFile(file);
        if (validation.ok === false) {
          const errorMessage = validation.error;
          console.error(errorMessage);
          const failId = `temp-fail-${Date.now()}-${file.name}`;
          setMessages(prev =>
            mergeMessages(prev, [
              {
                _id: failId,
                from_number: selectedPhoneNumber,
                content: errorMessage,
                message_type: 'document',
                received_at: new Date().toISOString(),
                direction: 'sent',
                status: 'failed',
              },
            ])
          );
          continue;
        }

        const mediaType = validation.mediaType;
        const tempId = `temp-media-${Date.now()}-${file.name}`;
        const fileCaption = index === 0 ? caption?.trim() || undefined : undefined;
        const tempMsg: InboxMessage = {
          _id: tempId,
          from_number: selectedPhoneNumber,
          content: fileCaption,
          message_type: mediaType,
          received_at: new Date().toISOString(),
          direction: 'sent',
          status: 'uploading',
        };
        setMessages(prev => mergeMessages(prev, [tempMsg]));

        try {
          const res = await InboxService.sendMedia(
            selectedPhoneNumber,
            file,
            fileCaption,
            isVoice && mediaType === 'audio'
          );
          const stableId = (res?.doc_id || res?.message_id) as string | undefined;
          setMessages(prev => {
            const normalized = prev.map(m =>
              m._id === tempId
                ? {
                    ...m,
                    _id: stableId || m._id,
                    media_url: res?.media_url || m.media_url,
                    message_type: res?.message_type || m.message_type,
                    content: fileCaption,
                    status: 'sent',
                    delivery_status: 'sent',
                  }
                : m
            );
            return dedupeAndSortMessages(normalized);
          });
        } catch (err: any) {
          const detail = mapMediaUploadError(err);
          setMessages(prev => {
            const normalized = prev.map(m =>
              m._id === tempId
                ? { ...m, status: 'failed', content: detail }
                : m
            );
            return dedupeAndSortMessages(normalized);
          });
        }
      }
    } finally {
      setSendingMedia(false);
    }
  };

  return {
    conversations,
    loadingConversations,
    incomingMessageTick,
    selectedPhoneNumber,
    setSelectedPhoneNumber,
    setConversationSearch,

    messages,
    loadingMessages,
    hasMore,
    loadMore,

    selectedConversation,
    sendMessage,
    sendMediaFiles,
    sendingMedia,
  };
};
